import React, { useState, useEffect } from 'react';
import get from 'lodash.get';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography } from '@kis-technologies/kis-ui';
import { selectors as configSelectors, Configuration } from 'services/configuration';
import { selectors as willCallSelectors, actions as willCallActions } from 'services/willcall';
import { useActions } from 'hooks/useActions';
import { ActionOverlay } from 'components/ActionOverlay';
import { ProcessingOverlay } from 'components/ProcessingOverlay';
import { MembershipLookup } from './components/MembershipLookup';
import CustomSearch from './components/CustomSearch';
import { useStyles } from './styled';
import startOfDay from 'date-fns/startOfDay';
import { useTranslation } from 'react-i18next';

const WillCallLookupView = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [showOverlay, setShowOverlay] = useState<boolean>(false);
  const [value, setValue] = useState<any>(null);
  const { getOrders, resetWillCall } = useActions({
    getOrders: willCallActions.getOrders,
    resetWillCall: willCallActions.resetWillCall
  });
  const loading = useSelector(willCallSelectors.selectLoading);
  const error = useSelector(willCallSelectors.selectError);
  const orderDetails = useSelector(willCallSelectors.selectOrderDetails);
  const ordersList = useSelector(willCallSelectors.selectOrdersList);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');
  const classes = useStyles({ fontFamily })();

  // On Mount - set will call active step to 0
  const selectedLookupOption = useSelector(willCallSelectors.selectedLookupOption);
  const configuration = useSelector(configSelectors.selectConfiguration) as Configuration;
  const support = get(configuration, `ticketing.supportName.${i18n.language}`) || t('commonText.support');
  const errorMessages = get(configuration, `willCall.errorMessages`);
  const errorOverlaySubheader =
    get(errorMessages, `subheader.${i18n.language}`) || t('commonText.ticketsTryAgain', { support });

  useEffect(() => {
    resetLookup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetLookup = () => {
    resetWillCall();
    setValue(null);
    setShowOverlay(false);
  };

  const submitAction = () => {
    if (value) {
      getOrders({
        ...value,
        startDate: startOfDay(new Date()).toISOString()
      });
    }
  };

  const onPrevPageClick = () => {
    // re-routes to the previous page (WillCallSelectionView)
    navigate('/willcall/selection', { state: { from: location.pathname } });
  };

  const navigateToNextpage = () => {
    if (ordersList.length) {
      navigate('/willcall/orders-list');
    } else if (orderDetails.length) {
      navigate('/willcall/orders');
    }
  };

  if (error && showOverlay) setShowOverlay(false);

  const LookupOptions = React.useCallback(() => {
    const screen = selectedLookupOption && selectedLookupOption.name;
    switch (screen) {
      case 'scanMembershipCard':
        return (
          <MembershipLookup key={`willcall-screen`} onNext={setValue} onPrev={onPrevPageClick} loading={loading} />
        );
      default:
        return <CustomSearch key={`willcall-screen`} onNext={setValue} onPrev={onPrevPageClick} loading={loading} />;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLookupOption]);

  return (
    <>
      {error && !loading && (
        <ActionOverlay
          title={
            <div className={classes.lookupViewTitle}>
              <Typography fontSize={64} fontWeight={600} align="center" sx={{ lineHeight: 1 }}>
                {error}
              </Typography>
            </div>
          }
          description={
            <Box width={710}>
              <Typography fontSize={30} align="center">
                {errorOverlaySubheader}
              </Typography>
            </Box>
          }
          confirmText={t('commonText.tryAgain')}
          height={630}
          onConfirm={resetLookup}
        />
      )}
      {value && !error && (
        <ProcessingOverlay
          processed={!!orderDetails.length || !!ordersList.length}
          processAction={() => setTimeout(() => submitAction(), 1000)}
          title={t('willCallLookup.lookingInformation')}
          onAnimationComplete={navigateToNextpage}
        />
      )}
      <LookupOptions />
    </>
  );
};

export default WillCallLookupView;
